// src/components/features/tours/TourLaunchIntent.js
//
// Consumes the article -> tour launch intent (45-CONTEXT.md D-05). The
// article page at /help/:slug renders OUTSIDE TourProvider (verified:
// TourProvider is at src/App.js ~L1804-1897, the help routes are at
// ~L1923-1936, above ProtectedRoute), so useTour() does not exist there.
// HelpArticlePage.js's "Take the interactive tour" CTA therefore navigates
// into the app carrying the tour id as a query param on the app root
// (`/?launchTour=<tourId>`) instead of calling launchTour() itself. This
// component, mounted inside TourProvider in src/App.js's appShell (right
// after WelcomeTourLauncher), consumes that intent on arrival.
//
// Reads window.location.search directly via URLSearchParams, NOT
// react-router's useSearchParams. Reason: TourContext.js itself reads
// window.location.pathname directly and TourProvider takes
// navigateToRoute as a prop rather than using router hooks, so this
// component stays mountable in tests/tourContext.test.js, which has no
// MemoryRouter.
//
// Modeled on WelcomeTourLauncher.js: a mount-once, one-shot, side-effect-only
// component that launches a tour and clears its trigger, rendering nothing.

import React from 'react';
import { useTour } from './TourContext';

export const LAUNCH_TOUR_PARAM = 'launchTour';

export default function TourLaunchIntent() {
    const { launchTour } = useTour();

    React.useEffect(() => {
        // Guard against a double-launch (React 18 StrictMode double-invokes
        // effects in development, and a re-entrant mount before this
        // instance's async work settles must not start the driver twice).
        // The param is read and stripped SYNCHRONOUSLY, before the
        // launchTour() await below, so even a second effect invocation --
        // in this instance or a re-entrant one -- finds nothing left in the
        // URL to act on. A ref latch alone would not protect a *separate*
        // component instance; the synchronous strip is what actually closes
        // that window.
        const url = new URL(window.location.href);
        if (!url.searchParams.has(LAUNCH_TOUR_PARAM)) return undefined;

        const tourId = url.searchParams.get(LAUNCH_TOUR_PARAM);
        url.searchParams.delete(LAUNCH_TOUR_PARAM);
        const remainingSearch = url.searchParams.toString();
        window.history.replaceState(
            {},
            '',
            url.pathname + (remainingSearch ? `?${remainingSearch}` : '') + url.hash,
        );

        if (!tourId) return undefined;

        // Swallow the result entirely -- no toast, no inline error, no
        // console.error. D-05: an unknown tour id here is a build-time-
        // prevented edge case (SC #3a's integrity test on relatedTourIds),
        // not a user-facing failure mode.
        launchTour(tourId).catch(() => {});
        return undefined;
        // Runs once per mount, by design -- a one-shot intent read, not a
        // reactive effect keyed on launchTour identity.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return null;
}
